import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useMsalProfile } from "../hooks/useMsalProfile";
import { useTranslation } from "../hooks/useTranslation";
import { LogoutIcon } from "./icons/LogoutIcon";
import { ProfileIcon } from "./icons/ProfileIcon";
import styles from "./UserMenu.module.css";

export const UserMenu = () => {
  const { t } = useTranslation();
  const { instance, account } = useAuth();
  const user = useMsalProfile();
  const [isOpen, setIsOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const fullName = user.fullName;
  const initials = user.initials;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    function handlePointerDown(event: PointerEvent) {
      if (!userMenuRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("pointerdown", handlePointerDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [isOpen]);

  const handleLogout = () => {
    setIsOpen(false);
    void instance.logoutRedirect({
      account: account ?? undefined,
    });
  };

  return (
    <div ref={userMenuRef} className={styles["userMenu"]}>
      <button
        type="button"
        className={classNames(styles["userChip"], isOpen && styles["userChipOpen"])}
        aria-label={fullName}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        onClick={() => setIsOpen((open) => !open)}
      >
        <div className={classNames(styles["userAvatar"], user.photoUrl ? undefined : styles["background"])}>
          {user.photoUrl ? (
            <img src={user.photoUrl} alt={fullName} className={styles["userAvatarImage"]} />
          ) : (
            initials
          )}
        </div>
      </button>

      {isOpen && (
        <div className={styles["userPanel"]} role="dialog" aria-label={t("nav.profile")}>
          <div className={styles["userInfoRow"]}>
            <dd className={styles["userInfoValue"]}>{fullName}</dd>
          </div>
          <div className={styles["userInfoRow"]}>
            <dd className={classNames(styles["userInfoValue"], styles["small"])}>{user.email || "-"}</dd>
          </div>
          <Link to="/profile" className={styles["userPanelLink"]} onClick={() => setIsOpen(false)}>
            <ProfileIcon className={styles["iconMedium"]} />
            {t("nav.profile")}
          </Link>
          <button type="button" onClick={handleLogout} className={styles["userPanelAction"]}>
            <LogoutIcon className={styles["iconMedium"]} />
            <span>{t("nav.logout")}</span>
          </button>
        </div>
      )}
    </div>
  );
};
